import { FilterConfig, FilterCondition, FilterGroup, FieldOption, APPLICANT_GROUP_FIELD, BooleanExprToFilterGroupConverter, FilterGroupToBooleanExprConverter } from '@aidkitorg/types/lib/admin-panel/filters';
import { BooleanExpr, ClickQuery } from '@aidkitorg/types/lib/survey';

const EMPTY_FILTER_GROUP: FilterGroup = {
  logic: 'and' as const,
  conditions: []
};

/**
 * Fetches an applicant group by ID and returns its filter configuration
 */
export type FetchApplicantGroupById = (id: string) => Promise<{ filters: FilterConfig }>;

/**
 * Adds the synthetic 'applicant_group' field to a list of available fields.
 *
 * @param fields - The base array of field options
 * @returns A new array with the applicant_group field appended
 */
export function addApplicantGroupField(fields: FieldOption[]): FieldOption[] {
  return [...fields, APPLICANT_GROUP_FIELD];
}

/**
 * Recursively expands applicant group references in a filter configuration.
 * This allows users to reference entire applicant group filters within other filters,
 * creating reusable filter templates.
 *
 * References to applicant groups that can't be resolved (the group was deleted) are DROPPED
 * rather than left in place: a deleted group must never be allowed to silently broaden a
 * filter. When dropping empties the root group, the returned config is disabled.
 *
 * @param filterConfig - The filter configuration to expand
 * @param fetchGroupById - Function to fetch applicant group data by ID
 * @returns Promise resolving to the expanded filter configuration
 */
export async function expandApplicantGroupReferences(
  filterConfig: FilterConfig,
  fetchGroupById: FetchApplicantGroupById
): Promise<FilterConfig> {
  if (!filterConfig.enabled || !filterConfig.config) {
    return filterConfig;
  }

  // Expand the root group
  const expandedGroup = await expandGroup(filterConfig.config, fetchGroupById);

  return {
    // A root group emptied by dropping deleted references is no longer a meaningful filter.
    enabled: expandedGroup.conditions.length > 0,
    config: expandedGroup
  };
}

/**
 * Recursively expands a FilterGroup, replacing applicant_group conditions with their filters
 * and dropping references that can't be resolved (deleted group, empty/invalid value, or a
 * conversion failure). FilterConfig has no "match nothing" leaf, so the only faithful way to
 * neutralize a deleted reference is to remove the condition (and prune nested groups it empties).
 */
async function expandGroup(
  group: FilterGroup,
  fetchGroupById: FetchApplicantGroupById
): Promise<FilterGroup> {
  if (!group.conditions || !Array.isArray(group.conditions)) {
    return EMPTY_FILTER_GROUP;
  }

  // `undefined` signals "drop this condition" so the parent removes it from the tree.
  const expandedConditions = await Promise.all(
    group.conditions.map(async (condition): Promise<FilterCondition | FilterGroup | undefined> => {
      // If this is a nested group, recursively expand it; drop it if it ends up empty.
      if ('logic' in condition) {
        const expandedSubGroup = await expandGroup(condition, fetchGroupById);
        return expandedSubGroup.conditions.length > 0 ? expandedSubGroup : undefined;
      }

      // If this is an applicant_group condition, fetch and inline it.
      if (isApplicantGroupCondition(condition)) {
        const groupId = getApplicantGroupConditionId(condition.value);
        if (!groupId) {
          return undefined;
        }

        try {
          const groupData = await fetchGroupById(groupId);

          if (groupData.filters && groupData.filters.enabled && groupData.filters.config) {
            const config = groupData.filters.config;

            // BooleanExpr format - convert to FilterGroup
            if ('kind' in config && !('logic' in config)) {
              const convertedGroup = BooleanExprToFilterGroupConverter.convert(config);
              return convertedGroup ?? undefined;
            }

            // Already in FilterGroup format
            return config;
          }

          // Group exists but has no filters configured — nothing to inline.
          return undefined;
        } catch {
          // Group was deleted/unresolvable — drop the reference.
          return undefined;
        }
      }

      // Regular condition, return as-is
      return condition;
    })
  );

  return {
    logic: group.logic,
    conditions: expandedConditions.filter(
      (c): c is FilterCondition | FilterGroup => c !== undefined
    )
  };
}

/**
 * Type guard to check if a condition is an applicant_group reference
 */
function isApplicantGroupCondition(condition: FilterCondition | FilterGroup): condition is FilterCondition {
  return 'field' in condition && condition.field === 'applicant_group';
}

/**
 * Extracts the applicant group ID from an applicant_group condition's value.
 * The value is normally a JSON string of the form `{ "id": "...", "name": "..." }`,
 * but may also be a raw id. Returns undefined when no usable id can be extracted.
 */
export function getApplicantGroupConditionId(value: string | undefined): string | undefined {
  if (!value || value.trim() === '') return undefined;
  try {
    const parsed = JSON.parse(value);
    if (parsed && typeof parsed.id === 'string' && parsed.id.trim() !== '') {
      return parsed.id;
    }
  } catch {
    // Not JSON — fall back to treating the raw value as the id.
  }
  return value.trim() === '' ? undefined : value;
}

/**
 * Collects every applicant group ID referenced by applicant_group conditions in a
 * FilterConfig (recursing into nested groups). Duplicates are removed.
 */
export function collectApplicantGroupIdsFromConfig(filterConfig: FilterConfig | undefined): string[] {
  if (!filterConfig?.config) return [];

  const ids = new Set<string>();
  const walk = (group: FilterGroup) => {
    for (const condition of group.conditions ?? []) {
      if ('logic' in condition) {
        walk(condition);
      } else if (isApplicantGroupCondition(condition)) {
        const id = getApplicantGroupConditionId(condition.value);
        if (id) ids.add(id);
      }
    }
  };
  walk(filterConfig.config);
  return Array.from(ids);
}

/**
 * Returns a copy of the FilterConfig with every applicant_group condition whose id is in
 * `idsToRemove` stripped out (recursing into nested groups). Nested groups left empty by
 * the removal are dropped. The `removed` flag indicates whether anything was pruned.
 *
 * Used to clean up filters that reference applicant groups which have since been deleted.
 */
export function removeApplicantGroupConditionsByIds(
  filterConfig: FilterConfig,
  idsToRemove: Set<string>
): { config: FilterConfig; removed: boolean } {
  if (!filterConfig.config || idsToRemove.size === 0) {
    return { config: filterConfig, removed: false };
  }

  let removed = false;
  const pruneGroup = (group: FilterGroup): FilterGroup => {
    const conditions: Array<FilterCondition | FilterGroup> = [];
    for (const condition of group.conditions ?? []) {
      if ('logic' in condition) {
        const prunedSubGroup = pruneGroup(condition);
        // Drop subgroups that became empty so we don't leave dangling no-op groups behind.
        if (prunedSubGroup.conditions.length > 0) {
          conditions.push(prunedSubGroup);
        }
      } else if (isApplicantGroupCondition(condition)) {
        const id = getApplicantGroupConditionId(condition.value);
        if (id && idsToRemove.has(id)) {
          removed = true;
          continue;
        }
        conditions.push(condition);
      } else {
        conditions.push(condition);
      }
    }
    return { logic: group.logic, conditions };
  };

  const prunedRoot = pruneGroup(filterConfig.config);
  if (!removed) {
    return { config: filterConfig, removed: false };
  }

  const stillHasConditions = prunedRoot.conditions.length > 0;
  return {
    config: stillHasConditions
      ? { enabled: filterConfig.enabled, config: prunedRoot }
      : { enabled: false, config: prunedRoot },
    removed: true
  };
}

/**
 * Checks if a BooleanExpr leaf node is an applicant_group reference.
 * Handles both old form ({ field: 'applicant_group' }) and new form
 * ({ value: { kind: 'Field', field: 'applicant_group' } }).
 */
function isApplicantGroupBooleanExpr(expr: BooleanExpr): boolean {
  if ('field' in expr && (expr as any).field === 'applicant_group') {
    return true;
  }
  if ('value' in expr && typeof (expr as any).value === 'object' && (expr as any).value?.kind === 'Field' && (expr as any).value?.field === 'applicant_group') {
    return true;
  }
  return false;
}

/**
 * Extracts the applicant group ID from a BooleanExpr leaf node that references an applicant group.
 * Returns the parsed group ID, or undefined if extraction fails.
 */
function extractApplicantGroupId(expr: BooleanExpr): string | undefined {
  let rawValue: string | undefined;

  // Old form: { field: 'applicant_group', kind: 'Equals', value: '...' }
  if ('field' in expr && 'value' in expr && typeof (expr as any).value === 'string') {
    rawValue = (expr as any).value;
  }
  // New form: extract from the comparison property that matches the node kind
  else if ('equals' in expr && (expr as any).equals?.kind === 'StringValue') {
    rawValue = (expr as any).equals.value;
  } else if ('notEqual' in expr && (expr as any).notEqual?.kind === 'StringValue') {
    rawValue = (expr as any).notEqual.value;
  } else if ('contains' in expr && (expr as any).contains?.kind === 'StringValue') {
    rawValue = (expr as any).contains.value;
  } else if ('lessThan' in expr && (expr as any).lessThan?.kind === 'StringValue') {
    rawValue = (expr as any).lessThan.value;
  } else if ('greaterThan' in expr && (expr as any).greaterThan?.kind === 'StringValue') {
    rawValue = (expr as any).greaterThan.value;
  }

  if (!rawValue || rawValue.trim() === '') return undefined;

  try {
    const parsed = JSON.parse(rawValue);
    if (parsed && parsed.id && typeof parsed.id === 'string' && parsed.id.trim() !== '') {
      return parsed.id;
    }
  } catch {
    // Not JSON, use raw value as ID
  }

  return rawValue;
}

/**
 * Recursively walks a BooleanExpr tree, expanding applicant_group references and DROPPING any
 * reference that can't be resolved (the group was deleted, has no filter, or forms a cycle).
 *
 * Dropping is done structurally — the parent And/Or removes the dead clause from its list and
 * collapses a lone survivor — so the result is the clean remaining expression with no leftover
 * placeholder nodes. A deleted group is therefore never able to silently broaden access.
 * Returns `undefined` to signal that the node resolved away entirely (e.g. every leaf was a
 * deleted group), which propagates up so an And/Or that loses all its clauses also drops out.
 *
 * Semantics:
 *  - "in group A OR in group B" with B deleted  -> "in group A"   (valid scope preserved)
 *  - "in group A AND in group B" with B deleted -> "in group A"   (the clause is removed)
 *  - sole "in group A" with A deleted           -> undefined      (no filter from this expr)
 *
 * `seen` guards against cyclic group references (group A's filter references group B's, etc.).
 */
async function expandBooleanExprInPlace(
  expr: BooleanExpr,
  fetchGroupById: FetchApplicantGroupById,
  seen: Set<string>
): Promise<BooleanExpr | undefined> {
  if (!expr || typeof expr !== 'object' || !('kind' in expr)) {
    return expr;
  }

  if (expr.kind === 'And' || expr.kind === 'Or') {
    const expanded = await Promise.all(
      (expr as any).clauses.map((clause: BooleanExpr) =>
        expandBooleanExprInPlace(clause, fetchGroupById, seen)
      )
    );
    const kept = expanded.filter((c): c is BooleanExpr => c !== undefined);
    if (kept.length === 0) return undefined;
    if (kept.length === 1) return kept[0];
    return { ...expr, clauses: kept } as BooleanExpr;
  }

  if (expr.kind === 'Not') {
    const inner = await expandBooleanExprInPlace((expr as any).clause, fetchGroupById, seen);
    if (inner === undefined) return undefined;
    return { ...expr, clause: inner } as BooleanExpr;
  }

  if (isApplicantGroupBooleanExpr(expr)) {
    const groupId = extractApplicantGroupId(expr);
    if (!groupId || seen.has(groupId)) return undefined;

    let groupData: { filters: FilterConfig };
    try {
      groupData = await fetchGroupById(groupId);
    } catch {
      // Group deleted/unresolvable — drop the reference rather than broadening the filter.
      return undefined;
    }

    if (!groupData.filters || !groupData.filters.enabled || !groupData.filters.config) {
      return undefined;
    }

    const config = groupData.filters.config;
    let groupExpr: BooleanExpr | undefined;
    if ('kind' in config && !('logic' in config)) {
      groupExpr = config as unknown as BooleanExpr;
    } else if ('logic' in config) {
      groupExpr = FilterGroupToBooleanExprConverter.convert(config as FilterGroup) ?? undefined;
    }
    if (!groupExpr) return undefined;

    const nextSeen = new Set(seen);
    nextSeen.add(groupId);
    return expandBooleanExprInPlace(groupExpr, fetchGroupById, nextSeen);
  }

  return expr;
}

/**
 * Expands applicant group references in a BooleanExpr filter, dropping references that can't be
 * resolved (deleted groups). Walks the BooleanExpr tree directly, avoiding the lossy
 * BooleanExpr → FilterGroup → BooleanExpr round-trip. Returns `undefined` when the whole
 * expression resolves away (so the caller applies no filter from it).
 *
 * @param booleanExpr - The BooleanExpr or ClickQuery filter to expand (or undefined)
 * @param fetchGroupById - Function to fetch applicant group data by ID
 * @returns Promise resolving to the expanded filter, or undefined if nothing remains
 */
export async function expandBooleanExprApplicantGroupReferences(
  booleanExpr: BooleanExpr | ClickQuery | undefined,
  fetchGroupById: FetchApplicantGroupById
): Promise<BooleanExpr | undefined> {
  if (!booleanExpr) {
    return undefined;
  }

  if ('kind' in booleanExpr && booleanExpr.kind === 'Click') {
    booleanExpr = booleanExpr.expr;
  }

  return expandBooleanExprInPlace(booleanExpr as BooleanExpr, fetchGroupById, new Set());
}